import { gsap } from "gsap";
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const backgroundTransitionTriggers = [];
const getThemeColor = (theme) => (theme === 'light' ? '4, 10, 29' : '255, 255, 255');

export function getSectionBackgroundColor(section) {
    if (section) {
        const computedStyle = window.getComputedStyle(section);
        const backgroundColor = computedStyle.getPropertyValue('background-color');
        return backgroundColor;
    }
    return null;
}

function getPreviousVisibleSibling(element) {
    let sibling = element.previousElementSibling;
    while (sibling) {
        if (sibling.checkVisibility()) {
            return sibling;
        }
        sibling = sibling.previousElementSibling;
    }
    return null;
}

function getNextVisibleSibling(element) {
    let sibling = element.nextElementSibling;
    while (sibling) {
        if (sibling.checkVisibility()) {
            return sibling;
        }
        sibling = sibling.nextElementSibling;
    }
    return null;
}

export const backgroundAnimation = (targets, initialColor, endColor) => {
    gsap.fromTo(targets, { backgroundColor: initialColor }, { backgroundColor: endColor });
};

function sectionBackgroundTransition() {
    killBackgroundTransitions();

    const allTransitionSections = document.querySelectorAll('section.bg-transition');

    allTransitionSections.forEach(bgTransitionSection => {
        const prevSection = getPreviousVisibleSibling(bgTransitionSection);

        // Safety check: Ensure prevSection exists before accessing classList
        const isPrevFeaturesParalax = prevSection ? prevSection.classList.contains('features-paralax') : false;

        if (isPrevFeaturesParalax) {
            return; // Already handled by featuresParalax.js
        }

        const nextSection = getNextVisibleSibling(bgTransitionSection);

        // --- Target Logic ---
        const targets = [bgTransitionSection];

        if (prevSection) {
            targets.push(prevSection);
        }

        if (nextSection) {
            targets.push(nextSection);
            if (nextSection.offsetHeight <= 800) {
                const nextNextSection = getNextVisibleSibling(nextSection);
                if (nextNextSection) {
                    targets.push(nextNextSection);
                }
            }
        }

        // Get colors.
        const prevSectionColor = getSectionBackgroundColor(prevSection);
        const transitionSectionColor = getSectionBackgroundColor(bgTransitionSection);
        const nextSectionColor = getSectionBackgroundColor(nextSection);

        const hideText = bgTransitionSection.querySelectorAll('.bg-transition-text-hide');
        const headerThemeColorForTransitionSection = '255, 255, 255';
        const headerThemeColorForNextSection = '4, 10, 29';

        // If any required section/color is missing, skip creation
        if (!prevSectionColor || !transitionSectionColor || !nextSectionColor) {
            return;
        }

        gsap.set(bgTransitionSection, { backgroundColor: prevSectionColor });

        const st = ScrollTrigger.create({
            trigger: bgTransitionSection,
            start: "top 90%",
            end: "bottom 60%",
            preventOverlaps: true,
            onEnter: () => {
                backgroundAnimation(targets, prevSectionColor, transitionSectionColor);
                if (hideText.length > 0) {
                    gsap.to(hideText, { opacity: 1, overwrite: 'auto' });
                }
            },
            onLeave: () => {
                backgroundAnimation(targets, transitionSectionColor, nextSectionColor);
                if (hideText.length > 0) {
                    gsap.to(hideText, { opacity: 0, overwrite: 'auto' });
                }
            },
            onEnterBack: () => {
                backgroundAnimation(targets, nextSectionColor, transitionSectionColor);
                if (hideText.length > 0) {
                    gsap.to(hideText, { opacity: 1, overwrite: 'auto' });
                }
            },
            onLeaveBack: () => {
                backgroundAnimation(targets, transitionSectionColor, prevSectionColor);

                // Restore the header state

                if (hideText.length > 0) {
                    gsap.to(hideText, { opacity: 0, overwrite: 'auto' });
                }
            }
        });

        backgroundTransitionTriggers.push(st);
    });
}

document.addEventListener('DOMContentLoaded', () => {
    sectionBackgroundTransition();
});

// --- Helper functions to control the triggers ---
/**
 * Disables all background transition ScrollTriggers.
 */
export function disableBackgroundTransitions() {
    backgroundTransitionTriggers.forEach(st => st.disable());
}

/**
 * Enables all background transition ScrollTriggers.
 */
export function enableBackgroundTransitions() {
    backgroundTransitionTriggers.forEach(st => st.enable());
}


/**
 * Kills and removes all background transition ScrollTriggers.
 * Useful for page transitions in a SPA.
 */
export function killBackgroundTransitions() {
    backgroundTransitionTriggers.forEach(st => st.kill());
    backgroundTransitionTriggers.length = 0; // Clear the array
}